import {useCallback, useEffect, useRef, useState} from 'react'
import {useIsRecording} from '@livekit/components-react'
import {saveRecording} from '@/shared/lib'
import * as recordingApi from './recordingApi'

type RecordingStatus = 'idle' | 'starting' | 'recording' | 'stopping'

/**
 * Must be used inside LiveKitRoom — syncs with useIsRecording() so we recover
 * when egress stops (by us or externally, e.g. room ended).
 */
export function useRecording(roomId: string) {
  const [status, setStatus] = useState<RecordingStatus>('idle')
  const [lastError, setLastError] = useState<string | null>(null)
  const [recordingStartedByMe, setRecordingStartedByMe] = useState(false)
  const egressIdRef = useRef<string | null>(null)
  const isRecordingFromLiveKit = useIsRecording()

  useEffect(() => {
    if (!isRecordingFromLiveKit) {
      egressIdRef.current = null
      setRecordingStartedByMe(false)
      setStatus(s => (s === 'recording' || s === 'stopping' ? 'idle' : s))
    } else {
      // Recording is on — either we started it or we joined into ongoing recording
      setStatus(s => (s === 'idle' ? 'recording' : s))
    }
  }, [isRecordingFromLiveKit])

  const startRecording = useCallback(async () => {
    if (status !== 'idle') return
    setLastError(null)
    setStatus('starting')
    try {
      const {egress_id, call_id} = await recordingApi.startRecording(roomId)
      egressIdRef.current = egress_id
      setRecordingStartedByMe(true)
      saveRecording({
        call_id,
        room_id: roomId,
        started_at: new Date().toISOString(),
      })
      setStatus('recording')
    } catch (err) {
      setStatus('idle')
      const msg = err instanceof Error ? err.message : String(err)
      setLastError(msg)
      throw err
    }
  }, [roomId, status])

  const stopRecording = useCallback(async () => {
    const egressId = egressIdRef.current
    if (status !== 'recording' || !egressId) return
    setLastError(null)
    setStatus('stopping')
    try {
      await recordingApi.stopRecording(egressId)
      egressIdRef.current = null
      setStatus('idle')
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      const alreadyStopped =
        msg.includes('EGRESS_ABORTED') ||
        msg.includes('cannot be stopped') ||
        msg.includes('failed_precondition')
      if (alreadyStopped) {
        egressIdRef.current = null
        setStatus('idle')
        return
      }
      setStatus('recording')
      setLastError(msg)
      throw err
    }
  }, [status])

  const isRecordingByOthers =
    isRecordingFromLiveKit && !recordingStartedByMe && status !== 'starting'

  return {
    status,
    startRecording,
    stopRecording,
    lastError,
    clearError: () => setLastError(null),
    isRecordingByOthers,
  }
}
